import { z } from 'zod';

import { isKnownTimezone } from '../../../lib/forms/timezones.js';

/**
 * The Tenants module's contracts.
 *
 * Mirrors the backend: every shape used by the list, detail, create, edit
 * and reference-data endpoints is declared here once, so the page and the
 * form cannot drift from each other or from the service.
 */

const MAX_NAME = 200;
const MAX_SLUG = 63;
const MAX_PHONE = 30;
const MAX_PHONE_EXTENSION = 10;
const MAX_ADDRESS = 500;
const MAX_TIMEZONE = 63;

const attachmentCredentialInput = z.object({
  AWS_REGION: z.string().trim().min(1, 'AWS region is required'),
  AWS_S3_BUCKET: z.string().trim().min(1, 'S3 bucket is required'),
  AWS_ACCESS_KEY_ID: z.string().trim().min(1, 'AWS access key is required'),
  AWS_SECRET_ACCESS_KEY: z.string().min(1, 'S3 secret access key is required'),
});

const optionalAttachmentCredentialInput = z
  .union([
    attachmentCredentialInput,
    z.object({
      AWS_REGION: z.literal(''),
      AWS_S3_BUCKET: z.literal(''),
      AWS_ACCESS_KEY_ID: z.literal(''),
      AWS_SECRET_ACCESS_KEY: z.literal(''),
    }),
  ])
  .transform((value) =>
    value.AWS_REGION === '' &&
    value.AWS_S3_BUCKET === '' &&
    value.AWS_ACCESS_KEY_ID === '' &&
    value.AWS_SECRET_ACCESS_KEY === ''
      ? undefined
      : value,
  );

export type AttachmentCredentialInput = z.infer<typeof attachmentCredentialInput>;

export const attachmentCredential = z.object({
  id: z.string(),
  tenantId: z.string(),
  provider: z.literal('s3'),
  credentials: attachmentCredentialInput,
  isActive: z.boolean(),
  version: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AttachmentCredential = z.infer<typeof attachmentCredential>;

/** One row in the tenants list. */
/**
 * One row in the tenants list.
 *
 * **Only `id` is guaranteed.** The list sends `?fields=` derived from the
 * columns the reader has chosen, and the server now honours it — so a field
 * behind a hidden column is genuinely absent from the response rather than
 * returned and ignored. Declaring any of them required makes the whole list
 * fail to parse the moment someone hides a column, which is what happened:
 * `countryId` and `createdAt` were required, neither is in the default
 * projection, and every load rendered "Something went wrong".
 *
 * `id` is exempt because `fieldsForColumns` always requests it — `rowKey` and
 * every row link need it whatever else was selected.
 */
export const tenantRow = z.object({
  id: z.string(),
  slug: z.string().optional(),
  name: z.string().optional(),
  status: z.string().optional(),
  timezone: z.string().optional(),
  enabledModules: z.array(z.string()).optional(),
  countryId: z.string().nullable().optional(),
  stateId: z.string().nullable().optional(),
  cityId: z.string().nullable().optional(),
  countryName: z.string().nullable().optional(),
  stateName: z.string().nullable().optional(),
  cityName: z.string().nullable().optional(),
  createdAt: z.string().optional(),
});

export type TenantRow = z.infer<typeof tenantRow>;

/** The full tenant shown on the detail screen and the edit form. */
/** One administrator a platform operator created for this tenant. */
export const tenantAdmin = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  status: z.string(),
});

export type TenantAdmin = z.infer<typeof tenantAdmin>;

export const tenantDetail = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  status: z.string(),
  timezone: z.string(),
  enabledModules: z.array(z.string()),
  /** Everyone a platform operator created for this tenant. May be empty. */
  admins: z.array(tenantAdmin).default([]),
  /** Where this tenant is reached; empty when it has no domain yet. */
  domains: z.array(z.object({ hostname: z.string(), isPrimary: z.boolean() })).default([]),
  /** The address to hand a new administrator. Null when there is no domain. */
  loginUrl: z.string().nullable().default(null),
  contactName: z.string().nullable(),
  phoneExtension: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  countryId: z.string().nullable().optional(),
  stateId: z.string().nullable().optional(),
  cityId: z.string().nullable().optional(),
  countryName: z.string().nullable().optional(),
  stateName: z.string().nullable().optional(),
  cityName: z.string().nullable().optional(),
  email: z.string().nullable(),
  version: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  deletedBy: z.string().nullable(),
});

export type TenantDetail = z.infer<typeof tenantDetail>;

/** What the create and edit forms collect. */
export const tenantFormInput = z.object({
  slug: z
    .string()
    .trim()
    .min(1, 'A short URL identifier is required')
    .max(MAX_SLUG)
    .regex(/^[a-z0-9-]+$/u, 'Use only lower-case letters, numbers and hyphens'),
  name: z.string().trim().min(1, 'A tenant name is required').max(MAX_NAME),
  status: z.enum(['PENDING', 'ACTIVE', 'SUSPENDED', 'ARCHIVED']),
  timezone: z
    .string()
    .trim()
    .min(1, 'A tenant needs a timezone')
    .max(MAX_TIMEZONE)
    .refine(isKnownTimezone, 'Choose a timezone from the list'),
  enabledModules: z.array(z.string()),
  contactName: z.string().max(MAX_NAME).or(z.literal('')),
  phoneExtension: z.string().max(MAX_PHONE_EXTENSION).or(z.literal('')),
  phone: z.string().max(MAX_PHONE).or(z.literal('')),
  address: z.string().max(MAX_ADDRESS).or(z.literal('')),
  countryId: z.uuid().or(z.literal('')),
  stateId: z.uuid().or(z.literal('')),
  cityId: z.uuid().or(z.literal('')),
  email: z.email().or(z.literal('')).optional(),
  /**
   * The first administrator. Collected on create only — provisioning makes the
   * tenant, its roles and this owner in one transaction, and a tenant created
   * without one has nobody who can ever sign into it.
   *
   * Optional here because the edit form shares this schema and cannot change
   * an owner. `tenantCreateInput` below makes it required where it matters.
   */
  /**
   * The administrators to create.
   *
   * A list on both forms: create collects the people who can sign in from day
   * one, and edit adds more. Optional on the shared shape because the edit
   * form submits an empty list when nobody is being added;
   * `tenantCreateInput` below requires at least one where it matters.
   */
  admins: z
    .array(
      z.object({
        email: z.email('A valid administrator email is required'),
        name: z.string().trim().min(1, 'An administrator name is required').max(MAX_NAME),
        password: z.string().or(z.literal('')).optional(),
      }),
    )
    .optional(),
  attachmentCredentials: optionalAttachmentCredentialInput.optional(),
});

/**
 * Mirrors `DEFAULT_PASSWORD_POLICY` in `@ibe/security`.
 *
 * Restated rather than imported because the policy lives in a server package
 * the portal does not depend on. The server validates independently and is the
 * authority — this exists so the operator sees the rule while typing instead of
 * after a round trip. If the two drift, the server wins and the form is merely
 * optimistic, which is the safe direction for a duplicate to fail in.
 */
/**
 * The create form's stricter schema.
 *
 * Storage credentials are required on create; owner accounts are managed
 * separately through the tenant-admins module.
 */
export const tenantCreateInput = tenantFormInput.extend({
  attachmentCredentials: attachmentCredentialInput,
  // Required on create: a tenant cannot store attachments until credentials
  // are supplied.
});

export type TenantFormInput = z.infer<typeof tenantFormInput>;

/** The create endpoint returns the newly created tenant. */
export const createdTenant = z.object({ id: z.string() });

export type CreatedTenant = z.infer<typeof createdTenant>;

/** Reference data for the country-state-city cascade. */
export const countryOption = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
  /** The dialling prefix, or null where the country has none. */
  phonePrefix: z.string().nullable(),
});

export type CountryOption = z.infer<typeof countryOption>;

export const stateOption = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
});

export type StateOption = z.infer<typeof stateOption>;

export const cityOption = z.object({
  id: z.string(),
  name: z.string(),
});

export type CityOption = z.infer<typeof cityOption>;

/**
 * One entry in the module catalogue.
 *
 * `enabledModules` above is `z.array(z.string())` rather than an enum built
 * from `MODULES`: the catalogue is served by the API, and a client-side enum
 * would reject a module the server shipped before this build did.
 */
export const moduleOption = z.object({
  code: z.string(),
  assignable: z.boolean(),
  permissions: z.array(z.string()),
});

export type ModuleOption = z.infer<typeof moduleOption>;
